/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Pulse › Tools & connectors (strategy 9.4; contract section 8): every subject from GET /v1/admin/pulse/subjects with its
 * state chip, success rate, p95, calls, top code, a 24-hour sparkline and its problem file; Release (a failing or degraded
 * tool reads recovering now), Mute and Unmute (admins, each behind a confirmation; never a quarantine); and the health
 * rules as sentences with the thresholds in force, plus the selected subject's numbers now. `?subject=` selects a row.
 */
import { useState } from 'react';
import { Plug, Search } from 'lucide-react';
import { api } from '../../lib/api';
import {
  HEALTH_CHIPS, SUBJECT_ACTION_LABELS, SUBJECT_KIND_LABELS, healthChip, healthRuleSentences, msText, pctText, pulseHref, sinceText, sortParam, subjectActionText, subjectActions,
  subjectName, toggleSort, topErrorText, type PulseHealthState, type PulseSubjectAction, type PulseSubjectKind, type PulseSubjectRow, type SortState,
} from '../../lib/pulse';
import { SaveNote } from '../../components/SaveNote';
import { Button, ConfirmDialog, EmptyState, Icon, Input, PageHeader, Panel, Select, Skeleton, Sparkline, Stack, StatusChip, Table, Tag, type Column } from '../../ui';
import { count } from '../../ui/format';
import { PageLink, PulseError, useLoad, usePagedList, useQueryParam } from './common';

const STATES: PulseHealthState[] = ['healthy', 'degraded', 'failing', 'quarantined', 'recovering'];
const KINDS: PulseSubjectKind[] = ['tool', 'mcp', 'mcp-server', 'receiver', 'connector', 'ppp-source'];
const TEXT_COLUMNS = new Set(['subject', 'state', 'topCode']);

function sparkTone(state: PulseHealthState): 'data' | 'warn' | 'bad' | 'ok' {
  if (state === 'failing' || state === 'quarantined') return 'bad';
  if (state === 'degraded') return 'warn';
  if (state === 'recovering') return 'ok';
  return 'data';
}

export function PulseTools({ canEdit }: { canEdit: boolean }) {
  const [state, setState] = useState('');
  const [kind, setKind] = useState('');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<SortState>({ key: 'state', direction: 'desc' });
  const [selected, setSelected] = useQueryParam('subject');
  const [pending, setPending] = useState<{ subjectId: string; action: PulseSubjectAction } | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  const params = { state: state || undefined, kind: kind || undefined, q: q.trim() || undefined, sort: sortParam(sort) };
  const list = usePagedList((cursor) => api.pulseSubjects({ ...params, cursor, limit: 100 }), JSON.stringify(params));
  const settings = useLoad(() => api.pulseSettings(), 'settings');
  const selectedRow = list.items.find((row) => row.subjectId === selected) ?? null;

  const run = async () => {
    if (!pending) return;
    setBusy(true);
    setActionError(null);
    try {
      const result = await api.pulseSubjectAction(pending.subjectId, pending.action);
      list.setItems((items) => items.map((row) => (row.subjectId === pending.subjectId ? { ...row, ...result.subject } : row)));
      setNote({ ok: true, text: `${subjectName(pending.subjectId).name}: ${SUBJECT_ACTION_LABELS[pending.action].toLowerCase()} done. The change is in the audit trail.` });
      setPending(null);
    } catch (error) {
      setActionError(error instanceof Error && error.message ? error.message : 'The service refused the change.');
    } finally {
      setBusy(false);
    }
  };

  const columns: Array<Column<PulseSubjectRow>> = [
    {
      key: 'subject', header: 'Subject', sortable: true,
      cell: (row) => {
        const name = subjectName(row.subjectId);
        return <span className="ui-cell-stack"><span><code>{name.name}</code>{name.server ? <Tag mono={false} title={`MCP server ${name.server}`}>{name.server}</Tag> : null}</span><span className="ui-cell-sub">{SUBJECT_KIND_LABELS[row.kind] ?? row.kind}{row.muted ? ' · muted' : ''}</span></span>;
      },
    },
    {
      key: 'state', header: 'State', sortable: true,
      cell: (row) => <span className="ui-cell-stack"><span><StatusChip tone={healthChip(row.state).tone}>{healthChip(row.state).label}</StatusChip></span>{row.since && row.state !== 'healthy' ? <span className="ui-cell-sub">{sinceText(row.since)}</span> : null}</span>,
    },
    { key: 'successPct', header: 'Success', numeric: true, sortable: true, cell: (row) => pctText(row.successPct) },
    { key: 'p95Ms', header: 'p95', numeric: true, sortable: true, cell: (row) => msText(row.p95Ms) },
    { key: 'calls24h', header: 'Calls 24 h', numeric: true, sortable: true, hideOnPhone: true, cell: (row) => count(row.calls24h) },
    {
      key: 'topCode', header: 'Top code', mono: true, hideOnPhone: true,
      cell: (row) => <span className="ui-cell-stack"><span>{row.topCode ?? '—'}</span>{row.topError ? <span className="ui-cell-sub pulse-top-error" title={row.topError.text}>{topErrorText(row.topError)}</span> : null}</span>,
    },
    { key: 'spark', header: 'Trend', hideOnPhone: true, cell: (row) => <Sparkline values={row.spark} label={`${subjectName(row.subjectId).name}: calls per hour, last 24 hours`} tone={sparkTone(row.state)} /> },
    { key: 'problem', header: 'Problem', hideOnPhone: true, cell: (row) => (row.problemId ? <PageLink href={pulseHref.problem(row.problemId)}>{row.problemId}</PageLink> : <span className="ui-quiet">—</span>) },
    ...(canEdit ? [{
      key: 'actions', header: <span className="ui-sr">Actions</span>, align: 'end' as const,
      cell: (row: PulseSubjectRow) => (
        <span className="ui-row-actions">
          {subjectActions(row).map((action) => (
            <Button
              key={action}
              size="sm"
              variant="ghost"
              onClick={(event) => { event.stopPropagation(); setActionError(null); setPending({ subjectId: row.subjectId, action }); }}
            >
              {SUBJECT_ACTION_LABELS[action]}
            </Button>
          ))}
        </span>
      ),
    } satisfies Column<PulseSubjectRow>] : []),
  ];

  const confirmText = pending ? subjectActionText(pending.action, pending.subjectId) : null;
  const rules = settings.load.state === 'ready' ? healthRuleSentences(settings.load.data.settings, selectedRow) : null;

  return (
    <>
      <PageHeader
        meta={<span>Every tool, MCP server and connected app Pulse measures, with its health. Counts only: nothing here names a person.</span>}
        actions={(
          <>
            <Select size="sm" className="ui-w-auto" aria-label="State" value={state} onChange={(event) => setState(event.target.value)}>
              <option value="">State: All</option>
              {STATES.map((value) => <option key={value} value={value}>{HEALTH_CHIPS[value].label}</option>)}
            </Select>
            <Select size="sm" className="ui-w-auto" aria-label="Kind" value={kind} onChange={(event) => setKind(event.target.value)}>
              <option value="">Kind: All</option>
              {KINDS.map((value) => <option key={value} value={value}>{SUBJECT_KIND_LABELS[value]}</option>)}
            </Select>
          </>
        )}
      />
      <div className="ui-page">
        <Stack>
          <Panel
            flush
            title="Subjects"
            description={list.load.state === 'ready' ? `${count(list.total)} subjects` : 'Tools, MCP tools and servers, connected apps, connectors and PPP sources'}
            actions={<Input size="sm" prefix={<Icon icon={Search} size="sm" />} aria-label="Search subjects" placeholder="Search by name…" value={q} onChange={(event) => setQ(event.target.value)} />}
            footer={canEdit ? <span>Pulse never switches a tool off (the owner, D5). Release ends a failing or degraded reading now (the tool reads recovering, healthy after a clean day); Mute keeps measuring but stops telling the model and stops the second-call refusal. Each asks first and is audited.</span> : <span>Auditors see every subject; Release and Mute are admin actions.</span>}
          >
            {list.load.state === 'failed' ? <div className="ui-panel-pad"><PulseError title="The subjects could not be loaded" error={list.load.error} onRetry={list.reload} retrying={list.reloading} /></div> : (
              <Table
                label="Subjects"
                columns={columns}
                rows={list.items}
                rowKey={(row) => row.subjectId}
                rowId={(row) => `subject-${row.subjectId.replace(/[^a-zA-Z0-9_-]/g, '_')}`}
                loading={list.load.state === 'loading'}
                skeletonRows={8}
                selectedKey={selected}
                onRowClick={(row) => setSelected(row.subjectId === selected ? null : row.subjectId)}
                sort={{ key: sort.key, direction: sort.direction, onSort: (key) => setSort(toggleSort(sort, key, !TEXT_COLUMNS.has(key))) }}
                more={{ cursor: list.next, loading: list.moreLoading, onMore: list.loadMore }}
                empty={<EmptyState compact icon={Plug} title={q || state || kind ? 'No subject matches' : 'Nothing measured yet'}>{q || state || kind ? 'Try another word or filter.' : 'Subjects appear here with the first tool call Pulse records.'}</EmptyState>}
              />
            )}
            <div className="ui-panel-pad ui-panel-pad--top ui-panel-pad--note"><SaveNote note={note} /></div>
          </Panel>

          <Panel title="Health rules" description={selectedRow ? `The rules behind the states, with the thresholds in force, and ${subjectName(selectedRow.subjectId).name} now` : 'The rules behind the states, with the thresholds in force; select a subject to see its numbers'} footer={<span>The thresholds and the lock mode are set under Pulse › Settings. Every transition is audited and appended to the subject's history.</span>}>
            {settings.load.state === 'loading' ? <div aria-busy="true"><Skeleton /><Skeleton /><Skeleton /></div> : null}
            {settings.load.state === 'failed' ? <PulseError title="The thresholds could not be read" error={settings.load.error} onRetry={settings.reload} retrying={settings.reloading} /> : null}
            {rules ? <ul className="ui-list" aria-label="Health rules">{rules.map((line, index) => <li key={index} className={index === 4 ? 'ui-text-warn' : undefined}>{line}</li>)}</ul> : null}
          </Panel>
        </Stack>
      </div>

      <ConfirmDialog
        open={pending !== null}
        title={confirmText?.title ?? ''}
        confirmLabel={confirmText?.confirm ?? 'Confirm'}
        tone="primary"
        busy={busy}
        error={actionError}
        onConfirm={() => void run()}
        onCancel={() => { setPending(null); setActionError(null); }}
      >
        {confirmText?.body}
      </ConfirmDialog>
    </>
  );
}
